import { Component } from '@angular/core';
import { ApiCollectionResponse, ApiViewData, DirectMessage, DropdownItem, UploadedFileInfo, User } from '../../app/app.interfaces';
import { ApiService } from '../../services/api.service';
import { Constants } from '../../app/constants';
import { ChatAdminUserFilter } from '../../app/enums';
import { ChatService } from '../../services/chat.service';
import { IonicHelper } from '../../helpers/ionic.helper';
import { UploadedFile } from '../../app/app.models';


@Component({
  selector: 'chat-admin-page',
  templateUrl: 'chat-admin-page.html'
})
export class ChatAdminPage {

  public senders: DropdownItem[] = [];
  public receivers: DropdownItem[] = [];
  public senderLoading: boolean = false;
  public receiverLoading: boolean = false;
  public localDate: Date = new Date();
  public locales;
  public senderCategory = ChatAdminUserFilter.SENDER;
  public receiverCategory = ChatAdminUserFilter.RECEIVER;
  public messages: DirectMessage[];
  public selectedMessage: number = -1;
  public editMessageContent: number = -1;
  
  public newMessage: string = '';
  
  public currentApiViewData: ApiViewData;
  public messagesLoading: boolean = false;
  public noDateSelected: boolean = false;
  
  private previousSearchTimeout;
  private searchCallCooldown: number = 1000;
  private pickedDate: Date;
  private pickedSender: DropdownItem;
  private pickedReceiver: DropdownItem;
  private isEditing: boolean = false;
  private MESSAGE_ID_PREFIX = '/users/';
  private lastSelectedDate: Date;

  constructor(private apiService: ApiService,
              private chatService: ChatService,
              private ionic: IonicHelper) {
    this.locales = {
      monday: Constants.WeekStartsMonday,
      weekdays: Constants.Weekdays,
      months: Constants.Months
    };
    this.lastSelectedDate = this.modifyTimeOfDate(new Date(), 0, 0, 0);
    const filters = {};
    filters['created[after]'] = this.lastSelectedDate.toLocaleString();
    this.loadAdminMessages(filters);
    this.setDropdownItemsByAdminFilter({ itemsPerPage: 50, 'order[firstName]': 'ASC' }, ChatAdminUserFilter.SENDER);
    this.setDropdownItemsByAdminFilter({ itemsPerPage: 50, 'order[firstName]': 'ASC' }, ChatAdminUserFilter.RECEIVER);
  }

  retrieveUsers(searchTerm: string, chatAdminFilter: ChatAdminUserFilter) {
    if (this.previousSearchTimeout) {
      clearTimeout(this.previousSearchTimeout);
    }
    
    this.previousSearchTimeout = setTimeout(() => {
      this.toggleLoading(true, chatAdminFilter);
      
      const filters = { itemsPerPage: 50, 'order[firstName]': 'ASC' };
      if (searchTerm.trim()) {
        filters['fullName'] = searchTerm;
      } else {
        this.setEmptyAdminUserFilter(chatAdminFilter);
        this.loadAdminMessages(this.getFilters());
      }
      
      this.setDropdownItemsByAdminFilter(filters, chatAdminFilter).then(() => {
        this.toggleLoading(false, chatAdminFilter);
      });
    }, this.searchCallCooldown);
  }
  
  toggleLoading(loading: boolean, category: ChatAdminUserFilter) {
    if (category === ChatAdminUserFilter.SENDER) {
      this.senderLoading = loading;
    } else if (category === ChatAdminUserFilter.RECEIVER) {
      this.receiverLoading = loading;
    }
  }
  
  saveDate(date: Date) {
    this.pickedDate = date;
    this.loadAdminMessages(this.getFilters());
  }
  
  private modifyTimeOfDate(date: Date, hours: number, minutes: number, seconds: number) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, seconds);
  }
  
  loadAdminMessages(filters: any = {}) {
    this.messagesLoading = true;
    this.chatService.getAdminMessages(filters).subscribe((response: ApiCollectionResponse) => {
      this.messages = response.items.filter(message => message.content !== '- verwijderd door beheerder -').reverse();
      this.currentApiViewData = response.view;
      this.messagesLoading = false;
    });
  }
  
  saveSelectedDropdownItem(item: DropdownItem, category: ChatAdminUserFilter) {
    if (category === ChatAdminUserFilter.SENDER) {
      this.pickedSender = item;
      this.loadAdminMessages(this.getFilters());
    } else if (category === ChatAdminUserFilter.RECEIVER) {
      this.pickedReceiver = item;
      this.loadAdminMessages(this.getFilters());
    }
  }

  toggleMessageIsEditable(index: number) {
    if (!this.isEditing) {
      if (this.selectedMessage === index) {
        this.selectedMessage = -1;
      } else {
        this.selectedMessage = index;
      }
    }
  }
  
  toggleMessageContentIsEditable(message: DirectMessage, index: number) {
    if (this.editMessageContent === index) {
      this.editMessageContent = -1;
    } else {
      this.editMessageContent = index;
      this.newMessage = message.content;
      this.isEditing = true;
    }
  }

  updateMessage(message: DirectMessage, messageContent: string, index: number) {
    if (message.content !== messageContent) {
      this.ionic.confirm('ADMIN.CHATS.ALERTS.EDIT_MESSAGE', undefined, 'GLOBAL.EDIT').subscribe((confirmed) => {
        if (confirmed) {
          this.chatService.setDirectMessage(message.id, {adminContent: messageContent}).subscribe(response => {
            message.content = messageContent;
            }, error => {
              this.ionic.alert('GLOBAL.GENERAL_ERROR');
            }
          );
        }
      });
    }
    this.toggleMessageContentIsEditable(message, index);
    this.isEditing = false;
  }
  
  removeMessage(removeMessage: DirectMessage) {
    this.ionic.confirm('ADMIN.CHATS.ALERTS.REMOVE_MESSAGE', undefined, 'GLOBAL.DELETE').subscribe((confirmed) => {
      if (confirmed) {
        this.chatService.removeDirectMessage(removeMessage.id).subscribe(response => {
          this.messages = this.messages.filter(message => message.id !== removeMessage.id);
          }, error => {
            this.ionic.alert('GLOBAL.GENERAL_ERROR');
          }
        );
      }
    });
  }

  loadNewerMessages() {
    if (this.currentApiViewData.pages && this.currentApiViewData.pages.next) {
      this.messagesLoading = true;
      const filters = this.getFilters();
      const receiverId: string = this.pickedReceiver ? (this.MESSAGE_ID_PREFIX + this.pickedReceiver.metadata.id) : null;
      filters['order[id]'] = 'ASC';
      this.chatService.getMessagesFromPage(receiverId, this.currentApiViewData.pages.next, filters).subscribe((response) => {
          this.messages = [...this.messages, ...response.items.reverse()];
          this.currentApiViewData = response.view;
          this.messagesLoading = false;
        }, error => {
          this.messagesLoading = false;
          this.ionic.alert('GLOBAL.GENERAL_ERROR');
        }
      );
    }
  }
  
  updateSelectedDateStyling(event) {
    const datePickerModal = document.getElementsByClassName('chat-admin-page__custom-datepicker-body')[0];
    
    if (!this.noDateSelected && this.lastSelectedDate.toString() === event.toString()) {
      this.noDateSelected = true;
      datePickerModal.classList.add('chat-admin-page__custom-datepicker-body--no-date-selected');
    } else {
      this.noDateSelected = false;
      datePickerModal.classList.remove('chat-admin-page__custom-datepicker-body--no-date-selected');
    }
    this.lastSelectedDate = event;
  }
  
  setDropdownItemsByAdminFilter(apiFilters: any, chatAdminFilter: ChatAdminUserFilter) {
    return this.apiService.getCollection('users', apiFilters).then((apiResponse: ApiCollectionResponse) => {
      const users: DropdownItem[] = apiResponse.items.map((user: User) => {
        return {
          title: user.fullName,
          metadata: user
        }
      });

      if (chatAdminFilter === ChatAdminUserFilter.SENDER) {
        this.senders = users;
      } else if (chatAdminFilter === ChatAdminUserFilter.RECEIVER) {
        this.receivers = users;
      }
    });
  }
  
  private setEmptyAdminUserFilter(adminFilter: ChatAdminUserFilter) {
    if (adminFilter === ChatAdminUserFilter.RECEIVER) {
      this.pickedReceiver = null;
    } else if (adminFilter === ChatAdminUserFilter.SENDER) {
      this.pickedSender = null;
    }
  }
  
  private getFilters() {
    const filters = {};
    const date: Date = !this.noDateSelected ? this.pickedDate : null;
    const sender: User = this.pickedSender ? this.pickedSender.metadata : null;
    const receiver: User = this.pickedReceiver ? this.pickedReceiver.metadata : null;
    
    if (date) {
      filters['created[after]'] = this.modifyTimeOfDate(date, 0, 0, 0).toLocaleString();
      filters['created[before]'] = this.modifyTimeOfDate(date, 23, 59, 59).toLocaleString();
    }

    if (sender) {
      filters['from'] = this.MESSAGE_ID_PREFIX + sender.id;
    }
    
    if (receiver) {
      filters['to'] = this.MESSAGE_ID_PREFIX + receiver.id;
    }
    
    return filters;
  }
  
  getAttachmentURL(attachment: UploadedFileInfo): string {
    return UploadedFile.createFromUploadedFileInfo(attachment).url;
  }
}




// WEBPACK FOOTER //
// ./src/components/chat-admin-page/chat-admin-page.ts